import { invariantResponse } from '@epic-web/invariant'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type MetaFunction, type LoaderFunctionArgs } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { useMemo, useState, useEffect } from 'react'

import { serverOnly$ } from 'vite-env-only/macros'
import { CosmeticDetails } from '#app/components/Cosmetics/CosmeticDetails'
import { CosmeticGallery } from '#app/components/Cosmetics/CosmeticGallery'
import { CosmeticImage } from '#app/components/Cosmetics/CosmeticImage'
import { CosmeticJsonLd } from '#app/components/Cosmetics/CosmeticJsonLd'
import { CosmeticVariants } from '#app/components/Cosmetics/CosmeticVariants'
import { RelatedCosmetics } from '#app/components/Cosmetics/RelatedCosmetics'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Button } from '#app/components/ui/button'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '#app/components/ui/tabs'
import { prisma } from '#app/utils/db.server.ts'
import { slugify } from '#app/utils/misc.tsx'
import {
	getCosmeticBySlug,
	getRelatedCosmeticsByType,
	getRelatedCosmeticsBySeason,
	getRelatedCosmeticsBySet,
} from './cosmetics/_cosmeticsQueries.server'
import { ImageDialog } from '#app/components/Cosmetics/ImageDialog'
import { CosmeticHero2 } from '#app/components/Cosmetics/CosmeticHero2.js'
import { CosmeticImages } from '#app/components/Cosmetics/CosmeticImages.js'

export const handle: SEOHandle = {
	getSitemapEntries: serverOnly$(async request => {
		const cosmetics = await prisma.cosmetic.findMany()
		return cosmetics.map(cosmetic => ({
			route: `/cosmetics/${slugify(cosmetic.name)}`,
			priority: 0.7,
		}))
	}),
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const cosmetic = data?.cosmetic
	if (!cosmetic) {
		return [
			{ title: 'Cosmetic Not Found' },
			{
				name: 'description',
				content: 'The requested cosmetic item could not be found.',
			},
		]
	}

	const cosmeticImages = JSON.parse(cosmetic.images) as {
		icon?: string
		featured?: string
		lego?: { large?: string }
	}

	const imageUrl =
		cosmeticImages.icon || cosmeticImages.featured || '/images/brand/logo.png'
	const fullImageUrl = new URL(imageUrl, 'https://lamaloot.com').toString()

	return [
		{ title: `${cosmetic.name} | Fortnite Cosmetic` },
		{
			name: 'description',
			content: `${cosmetic.name} - ${cosmetic.description}. Rarity: ${cosmetic.rarity}, Type: ${cosmetic.type}. Introduced in ${cosmetic.introducedIn}.`,
		},
		{
			name: 'keywords',
			content: `Fortnite, cosmetic, ${cosmetic.name}, ${cosmetic.type}, ${cosmetic.rarity}`,
		},
		{ property: 'og:title', content: `${cosmetic.name} | Fortnite Cosmetic` },
		{ property: 'og:description', content: cosmetic.description },
		{ property: 'og:image', content: fullImageUrl },
		{
			property: 'og:url',
			content: `https://lamaloot.com/cosmetics/${params.cosmeticSlug}`,
		},
		{ property: 'og:type', content: 'product' },
		{ property: 'og:site_name', content: 'Lamaloot' },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:site', content: '@LamalootOfficial' },
		{ name: 'twitter:title', content: `${cosmetic.name} | Fortnite Cosmetic` },
		{ name: 'twitter:description', content: cosmetic.description },
		{ name: 'twitter:image', content: fullImageUrl },
		{ name: 'twitter:image:alt', content: cosmetic.name },
		{ property: 'robots', content: 'index,follow' },
	]
}

export async function loader({ params }: LoaderFunctionArgs) {
	const cosmetic = await getCosmeticBySlug(params.cosmeticSlug!)
	invariantResponse(cosmetic, 'Not found', { status: 404 })

	const [relatedByType, relatedBySeason, relatedBySet] = await Promise.all([
		getRelatedCosmeticsByType(cosmetic.type, cosmetic.id),
		getRelatedCosmeticsBySeason(cosmetic.introducedIn, cosmetic.id),
		getRelatedCosmeticsBySet(cosmetic.set, cosmetic.id),
	])

	return { cosmetic, relatedByType, relatedBySeason, relatedBySet }
}

interface VariantOption {
	tag: string
	name: string
	image: string
}

interface VariantChannel {
	channel: string
	type: string
	options: VariantOption[]
}

interface MediaItem {
	type: 'image' | 'video'
	url: string
	variantName?: string
}

export default function CosmeticRoute() {
	const { cosmetic, relatedByType, relatedBySeason, relatedBySet } =
		useLoaderData<typeof loader>()
	const [selectedVariant, setSelectedVariant] = useState<string | null>(null)
	const [hoveredVariant, setHoveredVariant] = useState<string | null>(null)

	const cosmeticImages = JSON.parse(cosmetic.images) as {
		icon?: string
		featured?: string
		lego?: { large?: string }
	}

	const variants = useMemo(() => {
		if (!cosmetic.variants) return null
		return JSON.parse(cosmetic.variants) as VariantChannel[]
	}, [cosmetic.variants])

	const allMedia = useMemo(() => {
		const media: MediaItem[] = []
		if (cosmetic.video) {
			media.push({ type: 'video', url: cosmetic.video, variantName: 'Default' })
		}
		const images = [
			{
				url: cosmeticImages.icon,
				largeUrl: cosmeticImages.lego?.large,
				variantName: 'Default',
			},
			{
				url: cosmeticImages.featured,
				largeUrl: cosmeticImages.lego?.large,
				variantName: 'Default',
			},
			{
				url: cosmeticImages.lego?.small,
				largeUrl: cosmeticImages.lego?.large,
				variantName: 'Default',
			},
		]
		if (variants) {
			variants.forEach(variant => {
				variant.options.forEach(option => {
					media.push({
						type: 'image',
						url: option.image,
						largeUrl: option.image,
						variantName: option.name,
					})
				})
			})
		}
		media.push(
			...images
				.filter(
					(
						img,
					): img is { url: string; largeUrl?: string; variantName: string } =>
						Boolean(img.url),
				)
				.map(img => ({ type: 'image' as const, ...img })),
		)
		return media
	}, [cosmetic.video, cosmeticImages, variants])

	const currentVariant = hoveredVariant || selectedVariant

	return (
		<>
			<CosmeticJsonLd cosmetic={cosmetic} />
			<CosmeticHero2 cosmetic={cosmetic} />
			<div className="container mx-auto px-4 py-8">
				<div className="flex flex-col gap-8 lg:flex-row">
					<div className="lg:w-1/2">
						<CosmeticImage
							name={cosmetic.name}
							images={cosmeticImages}
							video={cosmetic.video}
							variants={variants}
							selectedVariant={currentVariant}
							onImageClick={url => setSelectedVariant(url)}
						/>
						{currentVariant && (
							<p className="mt-2 text-center text-lg font-semibold">
								{currentVariant}
							</p>
						)}
					</div>
					<div className="lg:w-1/2">
						{allMedia.length > 0 && (
							<CosmeticImages
								media={allMedia}
								onMediaClick={variantName => setSelectedVariant(variantName)}
								onMediaHover={variantName => setHoveredVariant(variantName)}
								selectedVariant={selectedVariant}
							/>
						)}
					</div>
				</div>
			</div>
			<div className="bg-gradient-to-br from-purple-700 to-indigo-900 p-4">
				<div className="container mx-auto px-4 py-8">
					<RelatedCosmetics
						byType={relatedByType}
						bySeason={relatedBySeason}
						bySet={relatedBySet}
					/>
				</div>
			</div>
		</>
	)
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>No cosmetic with the name "{params.cosmeticSlug}" exists</p>
				),
			}}
		/>
	)
}
